import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import incrementUnique from 'data/lib/increment-unique'
import { diff_obj } from 'data/lib/diff'

import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type ReportTag = {
  reporttagid: number
}
// private module logic //

const id: string = "reportTag"
const initialState: ApiResult< ReportTag[]> = { id, state: "init", value: [] }
const reportTagStream = new BehaviorSubject<ApiResult< ReportTag[]>>(initialState)
const reportTagEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let reportTagMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  reportTagStream.next({ id, state: 'ok', value: [] })
  reportTagMap = {}
}

function get(reporttagid: number):  ReportTag {
  return reportTagMap[reporttagid]
}

function copy(propertyid:number, reporttagid:number, code:string) {
  const currentReportTagVal:  ReportTag[] = reportTagStream.getValue().value;
  reportTagStream.next({ id, state: 'busy', value: currentReportTagVal });

  const payload = {table: 'reporttag', recordid: reporttagid, _synchronous: true, code: incrementUnique(code, "code", currentReportTagVal)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((reportTag:  ReportTag) => {
      const newRecord = reportTag[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.reporttagid),
        redofunc: () => copy(propertyid, reporttagid, code)
      });
      reportTagMap[newRecord.reporttagid] = newRecord
      // merge results unique by reporttagid
      const currentReportTagVal:  ReportTag[] = reportTagStream.getValue().value
      reportTagStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentReportTagVal, 'reporttagid').sort((a, b) => alphanum(a.code, b.code))
      })
      reportTagEventStream.next({ type: 'REPORTTAG_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      reportTagStream.next({ id, state: 'error', error: err, value: currentReportTagVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchReportTags(propertyid)
      return err
    })
}

function copies(propertyid: number, reporttagid: number, count: number) {
  const currentReportTagVal:  ReportTag[] = reportTagStream.getValue().value;
  reportTagStream.next({ id, state: 'busy', value: currentReportTagVal });

  const payload = {table: 'reporttag', recordid: reporttagid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((reporttags:  ReportTag[]) => {
      push_undo({
        undofunc: () => _.forEach(reporttags, newRecord => deleteOne(propertyid, newRecord.reporttagid)),
        redofunc: () => copies(propertyid, reporttagid, count)
      });
      _.forEach(reporttags, newRecord => reportTagMap[newRecord.reporttagid] = newRecord)
      // merge results unique by reporttagid
      const currentReportTagVal:  ReportTag[] = reportTagStream.getValue().value
      reportTagStream.next({
        id,
        state: 'ok',
        value: _.unionBy(reporttags, currentReportTagVal, 'reporttagid').sort((a, b) => alphanum(a.code, b.code))
      })
      reportTagEventStream.next({ type: 'REPORTTAG_COPIED', payload: reporttags })
      return reporttags
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      reportTagStream.next({ id, state: 'error', error: err, value: currentReportTagVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchReportTags(propertyid)
      return err
    })
}

function create(propertyid: number): Promise< ReportTag | { message: string }> {
  const currentValue:  ReportTag[] = reportTagStream.getValue().value
  reportTagStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    type: 'Recovery'
  }

  return Client.post(`reporttag`, payload).then((reportTags:  ReportTag) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, reportTags.reporttagid),
      redofunc: () => create(propertyid)
    })
    reportTagMap[reportTags.reporttagid] = reportTags
    // merge results unique by reporttagid
    const currentValue:  ReportTag[] = reportTagStream.getValue().value
    reportTagStream.next({
      id,
      state: 'ok',
      value: _.unionBy([reportTags], currentValue, 'reporttagid')
    })
    reportTagEventStream.next({ type: 'REPORTTAG_CREATED', payload: reportTags })
    return reportTags
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    reportTagStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchReportTags(propertyid)
    return err
  })
}

async function deleteOne(
  propertyid: number,
  reporttagid: number
): Promise<boolean | { message: string }> {
  const currentValue:  ReportTag[] = reportTagStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, reporttagid)
  const refreshFn = () => fetchReportTags(propertyid)
  if (!(await handleReferences('reporttag', reporttagid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted reporttagid
  const value = _(currentValue)
    .filter((p) => p.reporttagid !== reporttagid)
    .value()
  value.forEach((p) => (reportTagMap[p.reporttagid] = p))

  reportTagStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(reporttagid).then(() =>
  Client.del(`reporttag?reporttagid=eq.${reporttagid}`)
    .then((reportTags:  ReportTag) => {
      delete reportTagMap[reportTags.reporttagid]
      push_undo({
        undofunc: () => {
          Client.post('reportTags', reportTags)
          reportTagMap[reportTags.reporttagid] = reportTags
          reportTagStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, reporttagid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      reportTagStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchReportTags(propertyid)
      return err
    })
  )
}

function fetchReportTags(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise< ReportTag[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  reportTagStream.next({ id, state: 'busy', value: reportTagStream.getValue().value })

  let url = `reporttag?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `reporttag?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records:  ReportTag[]) => {
    _.forEach(records, (reportTags) => (reportTagMap[reportTags.reporttagid] = reportTags))
    // merge results unique by reporttagid
    reportTagStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function patch(reportTags: {
  reporttagid: number
}): Promise< ReportTag | { message: string }> {
  const currentValue:  ReportTag[] = reportTagStream.getValue().value
  const reporttagid: number = reportTags.reporttagid
  const currentReportTags = _.find(currentValue, { reporttagid })
  const changeset = diff_obj(reportTags, currentReportTags)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentReportTags),
    redofunc: () => patch(reportTags)
  })

  // optimistic update
  reportTagStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {reporttagid}), reportTags)
  })

  return _patches_.watch(reporttagid, Client.patch(`reporttag?reporttagid=eq.${reporttagid}`, changeset)
    .then((mf:  ReportTag[]) => {
      _.forEach(mf, (reportTags) => (reportTagMap[reportTags.reporttagid] = reportTags))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      reportTagStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentReportTags, 'propertyid')
      if (propertyid) fetchReportTags(propertyid)
      return err
    }))
}

// public module interface //

const reportTagService = {
  stream: () => reportTagStream.asObservable(),
  value: () => reportTagStream.getValue().value,
  eventStream: () => reportTagEventStream.asObservable(),
  copy,
  copies,
  clear,
  create,
  deleteOne,
  get,
  fetchReportTags,
  patch
}

export default reportTagService
